import { useCallback, useEffect, useState } from "react";
import { useBrand } from "@/lib/brand";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calendar, MapPin, Wallet, ArrowRight, Check, Clock, AlertCircle, PlayCircle, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { eur, fecha } from "@/lib/format";
import { importeTotal, pendienteAlquiler, restoTrasSenal } from "@/lib/reserva";
import { Button } from "@/components/ui/button";
import CatalogPicker from "@/components/CatalogPicker";
import LegalFooter from "@/components/public/LegalFooter";
import { toast } from "sonner";

export default function ClientWelcome() {
  const brand = useBrand();
  const { code } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  // El paso 1 queda bloqueado al enviar el formulario, así que si el pago
  // (último paso del wizard) quedó a medias este es el único camino de vuelta

  // Personalización ANTES de pagar: punto de recogida y devolución, pack de
  // km y extras. La señal se calcula con todo incluido (payment_plan.py);
  // hasta ahora esto solo existía dentro del pre-checkin, ya pasada la señal.
  const [catalogo, setCatalogo] = useState([]);
  const [seleccion, setSeleccion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [abierto, setAbierto] = useState(false);

  const paySignal = async () => {
    setPayLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/signal/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setPayLoading(false);
    }
  };

  const payDeposit = async () => {
    setPayLoading(true);
    try {
      const { data: resp } = await api.post(`/payments/checkout/session`, {
        reservation_code: code,
        origin_url: window.location.origin,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast.error(formatError(err));
      setPayLoading(false);
    }
  };

  const cargar = useCallback(
    ({ silencioso = false } = {}) =>
      api
        .get(`/r/${code}`)
        .then((res) => {
          setR(res.data);
          // La selección guardada manda: al volver de un pago o recargar, las
          // casillas tienen que aparecer como las dejó el cliente.
          const inicial = {};
          for (const it of res.data.selected_items || []) {
            if (it.product_id) inicial[it.product_id] = it.qty || 1;
          }
          setSeleccion(inicial);
          return res.data;
        })
        .catch((err) => {
          if (silencioso) return null;
          toast.error(formatError(err));
          navigate("/");
        }),
    [code, navigate],
  );

  useEffect(() => {
    cargar();
    api.get("/catalog").then(({ data }) => setCatalogo(data.products || [])).catch(() => {});
  }, [cargar]);

  const guardarSeleccion = async () => {
    setGuardando(true);
    try {
      const items = Object.entries(seleccion)
        .filter(([, q]) => q > 0)
        .map(([product_id, qty]) => ({ product_id, qty }));
      await api.post(`/r/${code}/items`, { items });
      await cargar({ silencioso: true });
      toast.success("Opciones guardadas");
      setAbierto(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setGuardando(false);
    }
  };

  if (!r)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">
        Cargando...
      </div>
    );

  // Reserva online sin confirmar, caducada o cancelada: solo se informa. El
  // backend tampoco admite pagos ni cambios con este código (booking/guards.py).
  if (r.payment_plan?.stage === "not_confirmed") {
    const texto =
      r.status === "pending_payment"
        ? r.signal_payment_status === "paid"
          ? "Hemos recibido tu pago y estamos confirmando la reserva. Vuelve a cargar esta página en unos minutos."
          : "Esta reserva aún no está confirmada: falta pagar la señal desde la página de pago que se abrió al reservar."
        : r.status === "expired"
          ? "Esta reserva caducó porque no se completó el pago de la señal. Si aún quieres el vehículo, haz una reserva nueva."
          : "Esta reserva está cancelada.";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0] px-4">
        <div className="max-w-md bg-white border border-[#DCD8D0] rounded-xl p-8" data-testid="welcome-not-confirmed">
          <h1 className="font-display text-2xl font-bold text-[#1A1A1A] mb-2">Reserva {r.code}</h1>
          <p className="text-[#5C5C5C] mb-4">{texto}</p>
          <Link to="/reservar" className="underline">Ver vehículos y fechas</Link>
        </div>
      </div>
    );
  }

  const dias = (() => {
    const ms = new Date(r.return_date) - new Date(r.pickup_date);
    return Number.isFinite(ms) ? Math.max(1, Math.round(ms / 86400000)) : 1;
  })();

  const canCheckin = !r.has_checkin;
  const canCheckout = r.has_checkin && !r.has_checkout;
  const allDone = r.has_checkin && r.has_checkout;

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="relative h-56 sm:h-72 overflow-hidden">
        {/* Sin fotos de bancos de imágenes externos: cada visita les daría la IP del cliente. */}
        <div className="absolute inset-0 bg-gradient-to-br from-marca-hover via-marca to-marca" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30" />
        <Link to="/" className="absolute top-4 left-4 text-white/90 text-sm hover:text-white" data-testid="welcome-home-link">
          ← Inicio
        </Link>
        <div className="absolute inset-x-0 bottom-0 p-6 text-white">
          <div className="container-x">
            <span className="text-overline text-white/90" data-testid="welcome-overline">Tu reserva · {r.code}</span>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2">
              Hola {r.client_name.split(" ")[0]} 👋
            </h1>
          </div>
        </div>
      </div>

      <main className="container-x py-8 max-w-2xl fade-in">
        <div className="bg-white border border-[#DCD8D0] rounded-xl overflow-hidden mb-6">
          <div className="p-5 grid grid-cols-2 gap-4 border-b border-[#DCD8D0]">
            <div>
              <div className="text-overline mb-1">Recogida</div>
              <div className="flex items-center gap-2 text-[#1A1A1A] font-medium">
                <Calendar className="h-4 w-4 text-marca" /> {fecha(r.pickup_date)}
              </div>
              <div className="text-sm text-[#5C5C5C] mt-0.5">{r.pickup_time} · {r.pickup_location}</div>
            </div>
            <div>
              <div className="text-overline mb-1">Devolución</div>
              <div className="flex items-center gap-2 text-[#1A1A1A] font-medium">
                <Calendar className="h-4 w-4 text-acento" /> {fecha(r.return_date)}
              </div>
              <div className="text-sm text-[#5C5C5C] mt-0.5">{r.return_time} · {r.return_location}</div>
            </div>
          </div>
          <div className="p-5 grid grid-cols-3 gap-4">
            <div>
              <div className="text-overline mb-1">Importe total</div>
              {/* Alquiler más los extras elegidos: total_amount es solo el alquiler. */}
              <div className="text-[#1A1A1A] font-medium" data-testid="welcome-total">
                {eur(importeTotal(r))}
              </div>
            </div>
            <div>
              <div className="text-overline mb-1">Fianza</div>
              <div className="flex items-center gap-2 text-[#1A1A1A] font-medium">
                <Wallet className="h-4 w-4" /> {r.deposit_amount} €
              </div>
            </div>
            <div className="text-right">
              <div className="text-overline mb-1">Km incluidos</div>
              <div className="text-[#1A1A1A] font-medium">{r.km_included_per_day} / día</div>
            </div>
          </div>
        </div>

        {allDone ? (
          <div className="bg-[#4A7C59]/10 border border-[#4A7C59]/30 rounded-xl p-6 text-center" data-testid="welcome-completed">
            <div className="h-12 w-12 mx-auto rounded-full bg-[#4A7C59] text-white flex items-center justify-center mb-3">
              <Check className="h-6 w-6" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1A1A1A] mb-1">¡Todo listo!</h3>
            <p className="text-sm text-[#5C5C5C]">
              Has completado el check-in y check-out. ¡Esperamos que hayas
              disfrutado de la aventura!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => canCheckin && navigate(`/checkin/${r.code}`)}
              disabled={!canCheckin}
              className={`w-full text-left bg-white border-2 rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 ${
                canCheckin
                  ? "border-marca hover:-translate-y-0.5 hover:shadow-md"
                  : "border-[#DCD8D0] opacity-60 cursor-default"
              }`}
              data-testid="welcome-checkin-btn"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                    r.has_checkin ? "bg-[#4A7C59]/15 text-[#4A7C59]" : "bg-marca text-white"
                  }`}
                >
                  {r.has_checkin ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[#1A1A1A]">
                    1. Pre-Checkin
                  </div>
                  <div className="text-sm text-[#5C5C5C]">
                    {r.has_checkin
                      ? "Completado ✓"
                      : "Tus datos para el contrato y firma online"}
                  </div>
                </div>
              </div>
              {!r.has_checkin && <ArrowRight className="h-5 w-5 text-marca shrink-0" />}
            </button>

            {r.self_service && r.has_checkin && !r.has_handover && (
              <button
                onClick={() => navigate(`/recogida/${r.code}`)}
                className="w-full text-left bg-white border-2 border-[#E89B33] rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                data-testid="welcome-selfpickup-btn"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-12 w-12 rounded-xl bg-[#E89B33] text-white flex items-center justify-center shrink-0">
                    <ArrowRight className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[#1A1A1A]">
                      Recogida autónoma
                    </div>
                    <div className="text-sm text-[#5C5C5C]">
                      ¿Ya estás en el punto de recogida? Abre la furgoneta sin
                      esperas desde tu móvil.
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#E89B33] shrink-0" />
              </button>
            )}

            {r.has_checkin && !r.has_checkout && (
              <button
                onClick={() => navigate(`/devolucion/${r.code}`)}
                className="w-full text-left bg-white border-2 border-[#4A7C59] rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                data-testid="welcome-returnprep-btn"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${r.return_checklist_completed ? "bg-[#4A7C59]/15 text-[#4A7C59]" : "bg-[#4A7C59] text-white"}`}>
                    {r.return_checklist_completed ? <Check className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[#1A1A1A]">Preparar la devolución</div>
                    <div className="text-sm text-[#5C5C5C]">
                      {r.return_checklist_completed
                        ? "Checklist completada ✓ · avisos de retraso o ampliación"
                        : "Checklist de devolución · avisar de retraso o ampliar días"}
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-[#4A7C59] shrink-0" />
              </button>
            )}

            <button
              onClick={() => canCheckout && navigate(`/checkout/${r.code}`)}
              disabled={!canCheckout}
              className={`w-full text-left bg-white border-2 rounded-xl p-5 flex items-center justify-between gap-4 transition-all duration-300 ${
                canCheckout
                  ? "border-acento hover:-translate-y-0.5 hover:shadow-md"
                  : "border-[#DCD8D0] opacity-60 cursor-default"
              }`}
              data-testid="welcome-checkout-btn"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div
                  className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                    !canCheckout ? "bg-[#EAE7E0] text-[#8A8A8A]" : "bg-acento text-white"
                  }`}
                >
                  {canCheckout ? <ArrowRight className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[#1A1A1A]">
                    2. Check-out (devolución)
                  </div>
                  <div className="text-sm text-[#5C5C5C]">
                    {!r.has_checkin
                      ? "Disponible tras el check-in"
                      : "Estado de devolución, incidencias y firma"}
                  </div>
                </div>
              </div>
              {canCheckout && <ArrowRight className="h-5 w-5 text-acento shrink-0" />}
            </button>
          </div>
        )}

        {r.has_checkin && brand?.guide_url && (
          <a
            href={brand.guide_url}
            target="_blank"
            rel="noreferrer"
            className="mt-6 block bg-marca hover:bg-marca-hover transition-colors rounded-xl p-5 text-white"
            data-testid="welcome-videos-card"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <PlayCircle className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-lg">
                  Vídeos: aprende a usar tu camper
                </div>
                <div className="text-sm text-white/80 mt-0.5">
                  Cocina, aguas, calefacción y el resto del equipamiento, en
                  vídeos cortos.
                </div>
              </div>
              <ExternalLink className="h-5 w-5 shrink-0 text-white/70" />
            </div>
          </a>
        )}

        {/* Personaliza el viaje. Se puede tocar mientras no esté pagado:
            en cuanto hay señal o extras cobrados, el backend lo bloquea. */}
        {catalogo.length > 0 && r.extras_payment_status !== "paid" && r.signal_payment_status !== "paid" && (
          <div className="bg-white border border-[#DCD8D0] rounded-xl p-5 mb-6" data-testid="welcome-opciones">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold text-[#1A1A1A]">Personaliza tu viaje</h3>
                <p className="text-sm text-[#5C5C5C] mt-0.5">
                  Punto de recogida y devolución, kilómetros y extras. Se suman al total antes de pagar.
                </p>
              </div>
              <button
                onClick={() => setAbierto((v) => !v)}
                className="text-sm font-semibold text-marca hover:underline shrink-0"
                data-testid="welcome-opciones-toggle"
              >
                {abierto ? "Ocultar" : "Ver opciones"}
              </button>
            </div>

            {(r.selected_items || []).length > 0 && !abierto && (
              <ul className="mt-3 space-y-1 text-sm text-[#5C5C5C]" data-testid="welcome-opciones-resumen">
                {(r.selected_items || []).map((it, i) => (
                  <li key={i} className="flex justify-between">
                    <span>{it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}</span>
                    <span>{eur(it.subtotal)}</span>
                  </li>
                ))}
              </ul>
            )}

            {abierto && (
              <div className="mt-4">
                <CatalogPicker products={catalogo} days={dias} value={seleccion} onChange={setSeleccion} />
                <div className="mt-4 flex justify-end">
                  <Button onClick={guardarSeleccion} disabled={guardando} className="bg-marca hover:bg-marca-hover text-white" data-testid="welcome-opciones-guardar">
                    {guardando ? "Guardando..." : "Guardar opciones"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Señal al reservar: es lo primero que se pide, sin esperar al
            check-in. El resto y la fianza, antes de la entrega (ver
            backend/payment_plan.py). */}
        {r.payment_plan?.stage === "signal" && (
          <div className="mt-6 bg-marca/10 border border-marca/30 rounded-xl p-4" data-testid="welcome-signal-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-marca shrink-0 mt-0.5" />
              <div className="text-sm text-[#1A1A1A]">
                Para confirmar tu reserva falta la señal de{" "}
                <strong>{eur(r.payment_plan.signal_amount)}</strong>. El resto
                ({eur(restoTrasSenal(r))}) y la fianza ({Number(r.deposit_amount).toFixed(0)} €)
                se pagan unos días antes de la entrega: te avisaremos por correo. La fianza se retiene en tu
                tarjeta sin cobrarla, salvo que al pagarla falten más de {r.payment_plan.hold_max_days ?? 6} días
                para la devolución: entonces se cobra y se te devuelve tras la devolución sin incidencias.
              </div>
            </div>
            <Button
              onClick={paySignal}
              disabled={payLoading}
              size="sm"
              className="mt-3 bg-marca hover:bg-marca-hover text-white"
              data-testid="welcome-signal-pay-btn"
            >
              {payLoading ? "Abriendo el pago..." : `Pagar señal ${eur(r.payment_plan.signal_amount)}`}
            </Button>
          </div>
        )}

        {r.payment_status === "pending" && r.has_checkin && r.payment_plan?.stage !== "signal" && (
          <div className="mt-6 bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4" data-testid="welcome-deposit-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-[#9C6512] shrink-0 mt-0.5" />
              <div className="text-sm text-[#9C6512]">
                La fianza ({eur(r.deposit_amount)}) aún está pendiente de pago.
              </div>
            </div>
            <Button
              onClick={payDeposit}
              disabled={payLoading}
              size="sm"
              className="mt-3 bg-marca hover:bg-marca-hover text-white"
              data-testid="welcome-deposit-pay-btn"
            >
              {payLoading ? "Abriendo el pago..." : "Pagar la fianza ahora"}
            </Button>
          </div>
        )}

        {/* Solo si de verdad queda alquiler por pagar: una señal que ya lo cubrió no deja «resto de 0 €». */}
        {pendienteAlquiler(r) > 0 && r.has_checkin && r.payment_plan?.stage !== "signal" && (
          <div className="mt-6 bg-[#E89B33]/10 border border-[#E89B33]/30 rounded-xl p-4" data-testid="welcome-rental-pending">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-[#9C6512] shrink-0 mt-0.5" />
              <div className="text-sm text-[#9C6512]">
                {r.signal_payment_status === "paid"
                  ? `Señal recibida. El resto (${eur(restoTrasSenal(r))}) se paga junto con la fianza antes de la entrega.`
                  : `El pago del alquiler (${eur(pendienteAlquiler(r))}) está pendiente.`}
              </div>
            </div>
            <Button
              onClick={() => navigate(`/checkin/${r.code}`)}
              size="sm"
              className="mt-3 bg-marca hover:bg-marca-hover text-white"
              data-testid="welcome-rental-pay-btn"
            >
              Completar pago
            </Button>
          </div>
        )}
      </main>
      <LegalFooter lang="es" brand={brand} />
    </div>
  );
}
